// src/pages/PublicPage.jsx
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export default function PublicPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      const q = query(collection(db, "posts"), where("slug", "==", slug));
      const snap = await getDocs(q);
      if (snap.empty) {
        setNotFound(true);
      } else {
        setPost({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div style={styles.root}>
        <div style={styles.loadingMsg}>파일 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={styles.notFoundIcon}>🔍</div>
          <div style={styles.notFoundTitle}>페이지를 찾을 수 없습니다</div>
          <div style={styles.notFoundSub}>링크가 만료되었거나 잘못된 주소입니다.</div>
        </div>
      </div>
    );
  }

  const sortedFiles = (post.files || []).sort((a, b) => a.order - b.order);

  return (
    <div style={styles.root}>
      <div style={styles.gridBg} />

      <div style={styles.card}>
        {/* Header */}
        <div style={styles.cardHeader}>
          <div style={styles.logoRow}>
            <div style={styles.logoMark}>DS</div>
            <div>
              <div style={styles.logoTitle}>DS ENGINEERING</div>
              <div style={styles.logoSub}>진단 보고서 파일함</div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div style={styles.metaGrid}>
          <div style={styles.metaItem}>
            <div style={styles.metaLabel}>진단 날짜</div>
            <div style={styles.metaValue}>{post.diagDate}</div>
          </div>
          <div style={styles.metaItem}>
            <div style={styles.metaLabel}>고객사</div>
            <div style={styles.metaValue}>{post.client}</div>
          </div>
          <div style={styles.metaItem}>
            <div style={styles.metaLabel}>수전용량</div>
            <div style={styles.metaValue}>{post.capacity}</div>
          </div>
          <div style={styles.metaItem}>
            <div style={styles.metaLabel}>파일 수</div>
            <div style={styles.metaValue}>{sortedFiles.length}개</div>
          </div>
        </div>

        {/* File list */}
        <div style={styles.fileSection}>
          <div style={styles.fileSectionTitle}>📁 파일 목록</div>
          {sortedFiles.length === 0 ? (
            <div style={styles.emptyMsg}>첨부된 파일이 없습니다.</div>
          ) : (
            sortedFiles.map((file, idx) => (
              <div key={idx} style={styles.fileItem}>
                <div style={styles.fileLeft}>
                  <span style={styles.fileNum}>{idx + 1}</span>
                  <div>
                    <div style={styles.fileName}>📄 {file.name}</div>
                  </div>
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.downloadBtn}
                  download
                >
                  ↓ 다운로드
                </a>
              </div>
            ))
          )}
        </div>

        <div style={styles.cardFooter}>
        DS ENGINEERING · 이 링크는 해당 고객사 전용입니다.
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif",
    position: "relative",
  },
  gridBg: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  loadingMsg: { color: "#6b7280", fontSize: "15px" },
  card: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "640px",
    position: "relative",
    zIndex: 1,
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
  },
  cardHeader: {
    padding: "28px 28px 20px",
    borderBottom: "1px solid #fde68a",
    background: "linear-gradient(135deg, #fffbeb, #fef9c3)",
  },
  logoRow: { display: "flex", alignItems: "center", gap: "14px" },
  logoMark: {
    width: "44px",
    height: "44px",
    background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "800",
    fontSize: "16px",
    flexShrink: 0,
  },
  logoTitle: { color: "#111827", fontSize: "18px", fontWeight: "700" },
  logoSub: { color: "#6b7280", fontSize: "12px", marginTop: "2px" },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "1px",
    background: "#fde68a",
    borderBottom: "1px solid #fde68a",
  },
  metaItem: {
    background: "#fffbeb",
    padding: "16px 20px",
  },
  metaLabel: { color: "#9ca3af", fontSize: "11px", fontWeight: "600", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "4px" },
  metaValue: { color: "#111827", fontSize: "15px", fontWeight: "600" },
  fileSection: { padding: "20px 24px" },
  fileSectionTitle: {
    color: "#6b7280",
    fontSize: "12px",
    fontWeight: "600",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "12px",
  },
  emptyMsg: { color: "#9ca3af", fontSize: "14px" },
  fileItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "14px 16px",
    marginBottom: "8px",
  },
  fileLeft: { display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" },
  fileNum: {
    width: "24px",
    height: "24px",
    background: "#f3f4f6",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    fontSize: "11px",
    fontWeight: "700",
    flexShrink: 0,
  },
  fileName: {
    color: "#111827",
    fontSize: "13px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  /* 다운로드 버튼: 파란색 */
  downloadBtn: {
    background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)",
    borderRadius: "6px",
    padding: "8px 16px",
    color: "#fff",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: "700",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  cardFooter: {
    padding: "14px 24px",
    borderTop: "1px solid #fde68a",
    color: "#9ca3af",
    fontSize: "11px",
    textAlign: "center",
    background: "#fffbeb",
  },
  notFoundIcon: { fontSize: "48px", textAlign: "center", padding: "40px 0 12px" },
  notFoundTitle: { color: "#111827", fontSize: "18px", fontWeight: "700", textAlign: "center", marginBottom: "8px" },
  notFoundSub: { color: "#6b7280", fontSize: "14px", textAlign: "center", paddingBottom: "40px" },
};
